/**
 * BUSINESS LOGIC (BL): PERSONAL SURFER (ESQUIVADOR PERSONAL - HU-03 / HU-06)
 * Lógica de negocio, helpers analíticos, sintetizador de audio nativo Web Audio API,
 * validación de formularios, feedback háptico y telemetría dual (Backend + GTM/GA4).
 * 
 * @module utilities/BL/PersonalSurfer
 */

import { DLTrackTelemetry } from '../DL/PersonalSurfer';

const STORAGE_SESSION_KEY = 'psl_surfer_participant_session';
const STORAGE_MUTE_KEY = 'psl_surfer_audio_muted';

/**
 * Resuelve la URL pública absoluta para assets estáticos y subidos.
 * 
 * @param {string|null} assetPath - Ruta relativa o URL del asset.
 * @returns {string} URL absoluta lista para img src o Canvas.
 */
export const resolveAssetUrl = (assetPath) => {
    if (!assetPath) return '';
    if (assetPath.startsWith('http://') || assetPath.startsWith('https://') || assetPath.startsWith('data:') || assetPath.startsWith('blob:')) {
        return assetPath;
    }
    const rawApiUrl = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';
    const hostUrl = rawApiUrl.replace(/\/api\/?$/, '');
    const cleanPath = assetPath.startsWith('/') ? assetPath : `/${assetPath}`;
    return `${hostUrl}${cleanPath}`;
};

/**
 * Sanitiza el identificador de sala.
 * 
 * @param {string} rawId 
 * @returns {string}
 */
export const sanitizeRoomId = (rawId) => {
    if (!rawId || typeof rawId !== 'string') return '';
    return rawId.replace(/[^a-zA-Z0-9\-_]/g, '').trim();
};

/**
 * Dispara telemetría dual hacia Google Tag Manager (dataLayer) y el Backend de Interaktia.
 * 
 * @param {Object} params
 * @param {string} params.roomId
 * @param {string} [params.sessionId]
 * @param {string} params.stepName - qr_scan, page_view, form_start, form_complete, game_start, game_complete
 * @param {string} [params.source='mobile_fan']
 * @param {Object} [params.additionalData={}]
 */
export const trackDualTelemetry = async ({
    roomId,
    sessionId = '',
    stepName,
    source = 'mobile_fan',
    additionalData = {}
}) => {
    try {
        if (!roomId || !stepName) return;

        // 1. Envío asíncrono no bloqueante al Backend .NET
        DLTrackTelemetry({
            roomId,
            sessionId: sessionId || `session_${Math.random().toString(36).substring(2, 10)}`,
            stepName,
            source
        }).catch(() => {});

        // 2. Google Tag Manager / Google Analytics 4 (dataLayer Push)
        if (typeof window !== 'undefined') {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: 'personal_surfer_step',
                step: stepName,
                roomId,
                sessionId,
                timestamp: new Date().toISOString(),
                ...additionalData
            });
        }
    } catch (err) {
        // Telemetría no bloqueante
        console.warn('[PersonalSurfer BL] Telemetry warning:', err);
    }
};

/**
 * Formatea un puntaje numérico con separador de miles.
 * 
 * @param {number|string} score
 * @returns {string} Ej: "3,850"
 */
export const formatScore = (score) => {
    const num = Number(score) || 0;
    return num.toLocaleString('es-MX');
};

/**
 * Enmascara un número telefónico para protección de privacidad (PII).
 * 
 * @param {string} phone
 * @returns {string} Ej: "55****1234"
 */
export const maskPhoneNumber = (phone) => {
    if (!phone || typeof phone !== 'string') return '-';
    const clean = phone.trim();
    if (clean.length < 7) return clean;
    const start = clean.substring(0, 2);
    const end = clean.substring(clean.length - 4);
    return `${start}****${end}`;
};

/**
 * Formatea segundos a tiempo legible.
 * 
 * @param {number} seconds
 * @returns {string} Ej: "30s"
 */
export const formatDuration = (seconds) => {
    const sec = Math.max(0, Number(seconds) || 0);
    return `${sec}s`;
};

/**
 * Determina la insignia de honor obtenida según el puntaje alcanzado.
 * 
 * @param {number} score 
 * @returns {{ title: string, icon: string, color: string }}
 */
export const getSurferBadge = (score) => {
    const s = Number(score) || 0;
    if (s >= 5000) {
        return { title: 'Leyenda del Asfalto', icon: '👑', color: '#ffd700' };
    }
    if (s >= 3000) {
        return { title: 'Surfista Supremo', icon: '🏄‍♂️', color: '#00e5ff' };
    }
    if (s >= 1500) {
        return { title: 'Acelerador Audaz', icon: '⚡', color: '#ff007f' };
    }
    return { title: 'Corredor Promesa', icon: '🎯', color: '#00ff88' };
};

// =========================================================================
// VALIDACIÓN DE FORMULARIOS DE REGISTRO
// =========================================================================

export const validateParticipantName = (name) => {
    if (!name || typeof name !== 'string') return false;
    const trimmed = name.trim();
    return trimmed.length >= 2 && trimmed.length <= 100;
};

export const validatePhone = (phone) => {
    if (!phone || typeof phone !== 'string') return false;
    const cleanDigits = phone.replace(/\D/g, '');
    return cleanDigits.length >= 7 && cleanDigits.length <= 15;
};

export const validateEmail = (email) => {
    if (!email || typeof email !== 'string') return true; // Opcional en esquivador si no es mandatorio
    const trimmed = email.trim().toLowerCase();
    if (trimmed.length === 0) return true;
    const regex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
    return regex.test(trimmed);
};

export const validateTerms = (accepted) => Boolean(accepted === true);
export const validatePrivacy = (accepted) => Boolean(accepted === true);

export const validateRegistrationForm = (formData = {}) => {
    const errors = {};

    if (!validateParticipantName(formData.participantName)) {
        errors.participantName = 'Ingresa tu nombre completo (mínimo 2 caracteres).';
    }

    if (!validatePhone(formData.phone)) {
        errors.phone = 'Ingresa un número de WhatsApp válido (al menos 7 a 10 dígitos).';
    }

    if (formData.email && !validateEmail(formData.email)) {
        errors.email = 'Ingresa un correo electrónico válido.';
    }

    if (!validateTerms(formData.acceptedTerms)) {
        errors.acceptedTerms = 'Debes aceptar los Términos y Condiciones.';
    }

    if (!validatePrivacy(formData.acceptedPrivacy)) {
        errors.acceptedPrivacy = 'Debes aceptar la Política de Privacidad.';
    }

    return {
        isValid: Object.keys(errors).length === 0,
        errors
    };
};

// =========================================================================
// GESTIÓN DE SESIÓN DE PARTICIPANTE
// =========================================================================

export const saveParticipantSession = (session) => {
    try {
        if (typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem(STORAGE_SESSION_KEY, JSON.stringify(session));
        }
    } catch {}
};

export const getParticipantSession = () => {
    try {
        if (typeof sessionStorage !== 'undefined') {
            const raw = sessionStorage.getItem(STORAGE_SESSION_KEY);
            return raw ? JSON.parse(raw) : null;
        }
        return null;
    } catch {
        return null;
    }
};

export const clearParticipantSession = () => {
    try {
        if (typeof sessionStorage !== 'undefined') {
            sessionStorage.removeItem(STORAGE_SESSION_KEY);
        }
    } catch {}
};

// =========================================================================
// SCREEN WAKE LOCK API
// =========================================================================

let screenWakeLockSentinel = null;
let wakeLockVisibilityHandler = null;

export const requestScreenWakeLock = async () => {
    try {
        if (typeof navigator === 'undefined' || !('wakeLock' in navigator)) {
            return false;
        }
        if (screenWakeLockSentinel && !screenWakeLockSentinel.released) {
            return true;
        }
        screenWakeLockSentinel = await navigator.wakeLock.request('screen');

        if (!wakeLockVisibilityHandler && typeof document !== 'undefined') {
            wakeLockVisibilityHandler = async () => {
                if (document.visibilityState === 'visible' && (!screenWakeLockSentinel || screenWakeLockSentinel.released)) {
                    try {
                        screenWakeLockSentinel = await navigator.wakeLock.request('screen');
                    } catch {}
                }
            };
            document.addEventListener('visibilitychange', wakeLockVisibilityHandler);
        }
        return true;
    } catch {
        return false;
    }
};

export const releaseScreenWakeLock = async () => {
    try {
        if (wakeLockVisibilityHandler && typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', wakeLockVisibilityHandler);
            wakeLockVisibilityHandler = null;
        }
        if (screenWakeLockSentinel) {
            await screenWakeLockSentinel.release();
            screenWakeLockSentinel = null;
            return true;
        }
        return false;
    } catch {
        screenWakeLockSentinel = null;
        return false;
    }
};

// =========================================================================
// FEEDBACK HÁPTICO (NAVIGATOR.VIBRATE)
// =========================================================================

export const triggerHapticFeedback = (patternOrType = 20) => {
    try {
        const nav = typeof window !== 'undefined' && window.navigator && typeof window.navigator.vibrate === 'function'
            ? window.navigator
            : (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function' ? navigator : null);

        if (!nav) return false;

        let pattern = 20;
        if (typeof patternOrType === 'string') {
            switch (patternOrType) {
                case 'swipe':
                case 'jump':
                case 'slide':
                    pattern = 15;
                    break;
                case 'coin':
                    pattern = 25;
                    break;
                case 'bonus':
                    pattern = [30, 40, 50];
                    break;
                case 'crash':
                case 'hit':
                    pattern = [80, 50, 100];
                    break;
                case 'victory':
                    pattern = [40, 40, 40, 80];
                    break;
                default:
                    pattern = 20;
            }
        } else {
            pattern = patternOrType;
        }

        return nav.vibrate(pattern);
    } catch {
        return false;
    }
};

// =========================================================================
// SINTETIZADOR DE AUDIO WEB AUDIO API (CERO ARCHIVOS MP3 PESADOS)
// =========================================================================

export const createSynthesizedAudioEngine = () => {
    let audioContext = null;
    let isMutedInternal = false;

    if (typeof sessionStorage !== 'undefined') {
        isMutedInternal = sessionStorage.getItem(STORAGE_MUTE_KEY) === 'true';
    }

    const getContext = () => {
        if (typeof window === 'undefined' && typeof global === 'undefined') return null;
        if (!audioContext) {
            const AudioContextClass = (typeof window !== 'undefined' ? (window.AudioContext || window.webkitAudioContext) : null) ||
                (typeof global !== 'undefined' ? (global.AudioContext || global.webkitAudioContext) : null);
            if (AudioContextClass) {
                try {
                    audioContext = new AudioContextClass();
                } catch {
                    audioContext = null;
                }
            }
        }
        return audioContext;
    };

    const unlock = async () => {
        try {
            const ctx = getContext();
            if (!ctx) return false;
            if (ctx.state === 'suspended') {
                await ctx.resume();
            }
            return ctx.state === 'running';
        } catch {
            return false;
        }
    };

    const setMuted = (muted) => {
        isMutedInternal = Boolean(muted);
        if (typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem(STORAGE_MUTE_KEY, isMutedInternal ? 'true' : 'false');
        }
    };

    const isMuted = () => isMutedInternal;

    const playCountBeep = (countNumber) => {
        if (isMutedInternal) return;
        const ctx = getContext();
        if (!ctx) return;
        if (ctx.state === 'suspended') ctx.resume().catch(() => {});

        const now = ctx.currentTime;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        const freq = countNumber === 1 || countNumber === 'GO' ? 880 : 440;
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, now);

        gain.gain.setValueAtTime(0.001, now);
        gain.gain.exponentialRampToValueAtTime(0.3, now + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, now + (countNumber === 'GO' ? 0.35 : 0.18));

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.start(now);
        osc.stop(now + (countNumber === 'GO' ? 0.36 : 0.19));
    };

    const playSwipe = () => {
        if (isMutedInternal) return;
        const ctx = getContext();
        if (!ctx) return;
        if (ctx.state === 'suspended') ctx.resume().catch(() => {});

        const now = ctx.currentTime;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = 'triangle';
        osc.frequency.setValueAtTime(250, now);
        osc.frequency.exponentialRampToValueAtTime(500, now + 0.08);

        gain.gain.setValueAtTime(0.15, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.09);

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.start(now);
        osc.stop(now + 0.09);
    };

    const playJump = () => {
        if (isMutedInternal) return;
        const ctx = getContext();
        if (!ctx) return;
        if (ctx.state === 'suspended') ctx.resume().catch(() => {});

        const now = ctx.currentTime;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = 'sine';
        osc.frequency.setValueAtTime(280, now);
        osc.frequency.exponentialRampToValueAtTime(700, now + 0.14);

        gain.gain.setValueAtTime(0.2, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.16);

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.start(now);
        osc.stop(now + 0.16);
    };

    const playSlide = () => {
        if (isMutedInternal) return;
        const ctx = getContext();
        if (!ctx) return;
        if (ctx.state === 'suspended') ctx.resume().catch(() => {});

        const now = ctx.currentTime;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(450, now);
        osc.frequency.exponentialRampToValueAtTime(180, now + 0.12);

        gain.gain.setValueAtTime(0.12, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.14);

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.start(now);
        osc.stop(now + 0.14);
    };

    const playCoin = () => {
        if (isMutedInternal) return;
        const ctx = getContext();
        if (!ctx) return;
        if (ctx.state === 'suspended') ctx.resume().catch(() => {});

        const now = ctx.currentTime;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = 'sine';
        osc.frequency.setValueAtTime(987.77, now); // B5
        osc.frequency.setValueAtTime(1318.51, now + 0.05); // E6

        gain.gain.setValueAtTime(0.001, now);
        gain.gain.exponentialRampToValueAtTime(0.25, now + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.18);

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.start(now);
        osc.stop(now + 0.19);
    };

    const playBonus = () => {
        if (isMutedInternal) return;
        const ctx = getContext();
        if (!ctx) return;
        if (ctx.state === 'suspended') ctx.resume().catch(() => {});

        const now = ctx.currentTime;
        const notes = [
            { freq: 523.25, offset: 0.00 },
            { freq: 659.25, offset: 0.06 },
            { freq: 783.99, offset: 0.12 },
            { freq: 1046.50, offset: 0.18 }
        ];

        notes.forEach(({ freq, offset }) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();

            osc.type = 'triangle';
            osc.frequency.setValueAtTime(freq, now + offset);

            gain.gain.setValueAtTime(0.001, now + offset);
            gain.gain.exponentialRampToValueAtTime(0.25, now + offset + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.001, now + offset + 0.2);

            osc.connect(gain);
            gain.connect(ctx.destination);

            osc.start(now + offset);
            osc.stop(now + offset + 0.22);
        });
    };

    const playCrash = () => {
        if (isMutedInternal) return;
        const ctx = getContext();
        if (!ctx) return;
        if (ctx.state === 'suspended') ctx.resume().catch(() => {});

        const now = ctx.currentTime;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(150, now);
        osc.frequency.exponentialRampToValueAtTime(40, now + 0.2);

        gain.gain.setValueAtTime(0.35, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.start(now);
        osc.stop(now + 0.26);
    };

    const playVictory = () => {
        if (isMutedInternal) return;
        playBonus();
    };

    const dispose = () => {
        if (audioContext) {
            try {
                audioContext.close();
            } catch {}
            audioContext = null;
        }
    };

    return {
        unlock,
        playCountBeep,
        playSwipe,
        playJump,
        playSlide,
        playCoin,
        playBonus,
        playCrash,
        playVictory,
        setMuted,
        isMuted,
        dispose
    };
};
